import type { FC } from 'react';
import { PrivacyStyles as styles } from '../css/styles';
import { Helmet } from 'react-helmet-async';

const PrivacyEU: FC = () => {
    return (
        <>
            <Helmet>
                <title>Privacy Policy — First Klass</title>
                <meta name="description" content="Privacy Policy for First Klass platform (www.first-klass.ru) — European Union (GDPR)" />
            </Helmet>
            <div className={styles.privacyContainer}>
                <h1>Privacy Policy</h1>
                <p>
                    This Privacy Policy explains how <strong>First Klass</strong> (
                    <strong>www.first-klass.ru</strong>, hereinafter «we», «us», or «the Controller»)
                    collects, uses, and protects the personal data of users located in the European
                    Economic Area (EEA) in accordance with Regulation (EU) 2016/679 (General Data
                    Protection Regulation — GDPR).
                </p>
                <p>
                    This Policy applies both to the First Klass website (www.first-klass.ru) and to
                    the First Klass mobile applications for iOS and Android (together, «the Platform»).
                    The Platform is used by students who take lessons with a tutor or school on First
                    Klass; student accounts are created by the tutor or school, not by public sign-up.
                </p>
                <p>
                    By using the Platform, you acknowledge that you have read and understood this
                    Privacy Policy. If you do not agree with any part of it, please discontinue
                    use of the Platform immediately.
                </p>

                <h2>1. Data Controller</h2>
                <p>
                    The data controller responsible for your personal data is the operator of
                    the First Klass platform. For all data protection enquiries, please contact
                    us via the contact form at www.first-klass.ru or by email at the address
                    published on the Platform.
                </p>

                <h2>2. Personal Data We Collect</h2>
                <p>We collect and process the following categories of personal data:</p>
                <ul>
                    <li>Identity data: first name, last name;</li>
                    <li>Contact data: email address, phone number;</li>
                    <li>Account data: username, encrypted password, account preferences;</li>
                    <li>
                        Learning content: written answers, homework and test responses you enter
                        when completing tasks in the app;
                    </li>
                    <li>
                        Audio recordings: voice recordings you make when completing speaking tasks
                        in the mobile app, which are stored so your tutor can review them;
                    </li>
                    <li>
                        Learning records: course progress, test scores, homework grades, lesson
                        attendance, lesson logs, and rewards (coins, badges, trophies);
                    </li>
                    <li>Usage data: pages visited, features used, session duration;</li>
                    <li>Technical data: IP address, browser type and version, device type, cookies.</li>
                </ul>
                <p>
                    We do not process special categories of personal data (Article 9 GDPR) such as
                    health data, racial or ethnic origin, or political opinions. Voice recordings
                    are used only to let your tutor review your spoken answers; they are not
                    analysed to identify you and are not used as biometric data.
                </p>
                <p>
                    Your learning content, audio recordings and learning records are visible within
                    the Platform only to your own tutor and the school or organisation through which
                    you were enrolled. They are not disclosed to any other third party.
                </p>

                <h2>3. Legal Bases for Processing (Article 6 GDPR)</h2>
                <p>We process your personal data on the following legal bases:</p>
                <ul>
                    <li>
                        <strong>Contract performance</strong> — to provide you access to the Platform
                        and deliver the services you have requested (Art. 6(1)(b));
                    </li>
                    <li>
                        <strong>Consent</strong> — for marketing communications, optional analytics,
                        and device permissions such as microphone access, where you have given
                        explicit consent (Art. 6(1)(a)). You can withdraw microphone access at any
                        time in your device settings;
                    </li>
                    <li>
                        <strong>Legitimate interests</strong> — to improve Platform security, prevent
                        fraud, and enhance user experience (Art. 6(1)(f));
                    </li>
                    <li>
                        <strong>Legal obligation</strong> — where processing is required to comply with
                        applicable EU or Member State law (Art. 6(1)(c)).
                    </li>
                </ul>

                <h2>4. Purposes of Processing</h2>
                <ul>
                    <li>Creating and managing your account on the Platform;</li>
                    <li>Delivering the educational services and course content you access;</li>
                    <li>Sending service notifications and responding to your enquiries;</li>
                    <li>Sending marketing communications (with your consent, withdrawable at any time);</li>
                    <li>Analysing Platform usage to improve performance and user experience;</li>
                    <li>Complying with legal and regulatory obligations.</li>
                </ul>

                <h2>5. Data Retention</h2>
                <p>
                    We retain your personal data only for as long as necessary to fulfil the
                    purposes described in this Policy, or as required by applicable law. Account
                    data is retained for the duration of your account. Upon account deletion,
                    personal data is erased or anonymised within 30 days, except where retention
                    is required by law.
                </p>

                <h2>6. Your Rights Under GDPR</h2>
                <p>As a data subject under the GDPR, you have the following rights:</p>
                <ul>
                    <li><strong>Right of access</strong> — to obtain a copy of your personal data (Art. 15);</li>
                    <li><strong>Right to rectification</strong> — to correct inaccurate or incomplete data (Art. 16);</li>
                    <li><strong>Right to erasure</strong> — to request deletion of your data («right to be forgotten», Art. 17);</li>
                    <li><strong>Right to restriction</strong> — to limit how we process your data in certain circumstances (Art. 18);</li>
                    <li><strong>Right to data portability</strong> — to receive your data in a structured, machine-readable format (Art. 20);</li>
                    <li><strong>Right to object</strong> — to object to processing based on legitimate interests or for direct marketing (Art. 21);</li>
                    <li><strong>Right to withdraw consent</strong> — at any time, without affecting lawfulness of prior processing;</li>
                    <li>
                        <strong>Right to lodge a complaint</strong> — with your national supervisory authority
                        (e.g. the ICO in the UK, CNIL in France, BfDI in Germany).
                    </li>
                </ul>
                <p>
                    To exercise any of these rights, please contact us through the Platform's
                    contact form. We will respond within 30 days.
                </p>

                <h2>7. Cookies and Tracking</h2>
                <p>
                    The Platform uses cookies and similar technologies. Non-essential cookies
                    (analytics, marketing) are only set with your explicit consent via our cookie
                    consent banner. You may withdraw consent or manage preferences at any time
                    through your browser settings or the Platform's cookie settings panel.
                </p>

                <h2>8. Mobile Applications and Device Permissions</h2>
                <p>
                    The First Klass mobile applications request the following device permission:
                </p>
                <ul>
                    <li>
                        <strong>Microphone</strong> — requested only when you start a speaking task,
                        so the app can record your spoken answer. Recordings are uploaded to First
                        Klass and made available to your tutor for review and assessment. They are
                        not used for any other purpose, not used to identify you by voice, and not
                        shared with any third party. You can decline or later revoke this permission
                        in your device settings; speaking tasks will then be unavailable, but the
                        rest of the app continues to work.
                    </li>
                </ul>
                <p>
                    The mobile applications do <strong>not</strong> use Yandex.Metrica or any other
                    third-party analytics, advertising, or tracking technologies, and do not collect
                    any advertising identifier. The cookie and analytics practices described in
                    Section 7 apply to the website only.
                </p>
                <p>
                    You can permanently delete your account, together with your audio recordings and
                    learning content, from within the app (Profile → Delete Account). Deletion is
                    confirmed by an email link and completes as described in Section 5.
                </p>

                <h2>9. International Data Transfers</h2>
                <p>
                    If your data is transferred outside the EEA, we ensure an adequate level of
                    protection through Standard Contractual Clauses (SCCs) approved by the European
                    Commission, or other mechanisms permitted under Chapter V of the GDPR.
                </p>

                <h2>10. Third-Party Processors</h2>
                <p>
                    We may share your data with trusted third-party service providers acting as
                    data processors (e.g. hosting, analytics, payment processing). All processors
                    are bound by data processing agreements compliant with Article 28 GDPR and
                    may only process data according to our documented instructions.
                </p>

                <h2>11. Changes to This Policy</h2>
                <p>
                    We may update this Privacy Policy from time to time. Material changes will be
                    communicated via a notice on the Platform or by email. The current version is
                    always available at <strong>www.first-klass.ru</strong>.
                </p>
                <p>
                    <strong>Version date: 31 August 2026</strong>
                </p>
            </div>
        </>
    );
};

export default PrivacyEU;
